import { useState, useRef } from 'react';
import WizardModal, { WizardButton, WizardLabel, WizardInput, WizardSelect, WizardSuccess, WizardError } from './WizardModal';
import { post } from '../../api';

// ── Types (matches backend ai-estimate-meal response) ──

interface NutritionPer100 {
  kcal: number;
  protein_g: number;
  fat_g: number;
  carbs_g: number;
  fiber_g: number;
  b12_mcg: number;
  iron_mg: number;
  vitd_mcg: number;
  calcium_mg: number;
  zinc_mg: number;
  omega3_mg: number;
  iod_mcg: number;
}

interface EstimateResponse {
  product_name: string;
  brand?: string | null;
  description: string;
  nutrition_per_100: NutritionPer100;
  unit: string;
  portion_size_g: number | null;
  portion_label: string | null;
  confidence: 'high' | 'medium' | 'low';
  source: string;
  product_id?: number;
  recipe_id?: number;
  cost_usd: number;
}

type Step = 'input' | 'confirm' | 'saved';

const MEAL_TYPES = [
  { value: 'breakfast', label: 'Frühstück' },
  { value: 'lunch', label: 'Mittagessen' },
  { value: 'dinner', label: 'Abendessen' },
  { value: 'snack', label: 'Snack' },
];

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function MealEstimateWizard({ open, onClose }: Props) {
  // Step 1: Input
  const [description, setDescription] = useState('');
  const [mealType, setMealType] = useState('lunch');
  const [date, setDate] = useState(todayISO());
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [imageType, setImageType] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Step 2: Confirm — amount in grams (or portion count)
  const [estimate, setEstimate] = useState<EstimateResponse | null>(null);
  const [amountG, setAmountG] = useState('');
  const [saveAsProduct, setSaveAsProduct] = useState(false);

  // State
  const [step, setStep] = useState<Step>('input');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Computed nutrition from per_100 + amountG ──

  const computedNutrition = estimate ? (() => {
    const g = parseFloat(amountG.replace(',', '.')) || (estimate.portion_size_g || 100);
    const factor = estimate.unit === 'portion' ? g : g / 100;
    const n = estimate.nutrition_per_100;
    return {
      kcal: Math.round(n.kcal * factor),
      protein_g: Math.round(n.protein_g * factor),
      fat_g: Math.round(n.fat_g * factor),
      carbs_g: Math.round(n.carbs_g * factor),
      fiber_g: Math.round(n.fiber_g * factor),
    };
  })() : null;

  // ── Image handling ──

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setImagePreview(dataUrl);
      const [header, base64] = dataUrl.split(',');
      const mime = header.match(/data:(.*?);/)?.[1] || 'image/jpeg';
      setImageBase64(base64);
      setImageType(mime);
    };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImageBase64(null);
    setImageType(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Step 1 → Step 2: Estimate ──

  const handleEstimate = async () => {
    if (!description.trim() && !imageBase64) return;
    setLoading(true);
    setError(null);

    try {
      const result = await post<EstimateResponse>('/ai/estimate-meal', {
        description: description.trim() || undefined,
        image_base64: imageBase64 || undefined,
        image_type: imageType || undefined,
        meal_type: mealType,
      });
      setEstimate(result);
      // Default amount: portion size if available, otherwise 100
      setAmountG(result.portion_size_g ? String(result.portion_size_g) : result.unit === 'portion' ? '1' : '100');
      setSaveAsProduct(result.source === 'ai_estimate' || result.source === 'image_recognition');
      setStep('confirm');
    } catch (err: unknown) {
      setError((err as { message?: string })?.message || 'Schätzung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: Reject ──

  const handleReject = async () => {
    if (!description.trim() && !imageBase64) { setStep('input'); return; }
    setLoading(true);
    setError(null);
    try {
      const result = await post<EstimateResponse>('/ai/estimate-meal', {
        description: description.trim() ? `NICHT: ${estimate?.product_name}. Gemeint ist: ${description.trim()}` : undefined,
        image_base64: imageBase64 || undefined,
        image_type: imageType || undefined,
        meal_type: mealType,
      });
      setEstimate(result);
      setAmountG(result.portion_size_g ? String(result.portion_size_g) : '100');
      setSaveAsProduct(true);
    } catch (err: unknown) {
      setError((err as { message?: string })?.message || 'Schätzung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2 → Step 3: Save ──

  const handleConfirm = async () => {
    if (!estimate || !computedNutrition) return;
    setLoading(true);
    setError(null);

    const g = parseFloat(amountG.replace(',', '.')) || 100;

    try {
      await post('/tracking', {
        date,
        meal_type: mealType,
        description: estimate.brand ? `${estimate.product_name} (${estimate.brand})` : estimate.product_name,
        kcal: computedNutrition.kcal,
        protein_g: computedNutrition.protein_g,
        fat_g: computedNutrition.fat_g,
        carbs_g: computedNutrition.carbs_g,
        fiber_g: computedNutrition.fiber_g,
        product_id: estimate.product_id || undefined,
        recipe_id: estimate.recipe_id || undefined,
        amount_g: estimate.unit !== 'portion' ? g : undefined,
        servings: estimate.unit === 'portion' ? g : undefined,
      });

      // Save as food_item for future use
      if (saveAsProduct && !estimate.product_id && !estimate.recipe_id) {
        try {
          const n = estimate.nutrition_per_100;
          await post('/food-items', {
            name: estimate.product_name,
            brand: estimate.brand || null,
            is_product: !!estimate.brand,
            category: 'Sonstiges',
            unit: estimate.unit || 'g',
            kcal_per_100: n.kcal,
            protein_g_per_100: n.protein_g,
            fat_g_per_100: n.fat_g,
            carbs_g_per_100: n.carbs_g,
            fiber_g_per_100: n.fiber_g,
            b12_mcg_per_100: n.b12_mcg,
            iron_mg_per_100: n.iron_mg,
            vitd_mcg_per_100: n.vitd_mcg,
            calcium_mg_per_100: n.calcium_mg,
            zinc_mg_per_100: n.zinc_mg,
            omega3_mg_per_100: n.omega3_mg,
            iod_mcg_per_100: n.iod_mcg,
            portion_size_g: estimate.portion_size_g,
            portion_label: estimate.portion_label,
            source: 'ai',
          });
        } catch { /* non-critical */ }
      }

      setStep('saved');
      window.dispatchEvent(new CustomEvent('plate:data-changed'));
    } catch (err: unknown) {
      setError((err as { message?: string })?.message || 'Speichern fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  // ── Reset ──

  const resetWizard = () => {
    setDescription(''); setMealType('lunch'); setDate(todayISO());
    removeImage(); setEstimate(null); setStep('input');
    setError(null); setLoading(false); setAmountG('');
  };

  const handleClose = () => { resetWizard(); onClose(); };
  const handleTrackAnother = () => { resetWizard(); };

  // ── Render: Saved ──

  if (step === 'saved') {
    return (
      <WizardModal open={open} onClose={handleClose} title="Getrackt!" footer={null}>
        <WizardSuccess message={`${estimate?.product_name} getrackt!`} onDone={handleClose} />
        <div className="text-center">
          <button onClick={handleTrackAnother} className="px-4 py-2 bg-emerald-100 text-emerald-700 rounded-xl text-sm font-medium hover:bg-emerald-200 transition-colors">
            Noch etwas tracken
          </button>
        </div>
      </WizardModal>
    );
  }

  // ── Render: Confirm ──

  if (step === 'confirm' && estimate && computedNutrition) {
    return (
      <WizardModal
        open={open}
        onClose={handleClose}
        title="Stimmt das?"
        footer={
          <div className="flex gap-2">
            <button onClick={handleReject} disabled={loading}
              className="flex-1 py-3 bg-gray-100 text-gray-600 rounded-xl font-medium text-sm hover:bg-gray-200 transition-colors disabled:opacity-50">
              Nein, das nicht
            </button>
            <WizardButton onClick={handleConfirm} loading={loading} disabled={false}>
              Tracken
            </WizardButton>
          </div>
        }
      >
        {error && <WizardError message={error} />}

        {/* Product identification */}
        <div className="bg-white rounded-xl border border-gray-100 p-3">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-lg">
              {estimate.source === 'known_product' ? '📦' : estimate.source === 'recipe' ? '📖' : estimate.source === 'image_recognition' ? '📷' : '🤖'}
            </span>
            <div>
              <span className="font-medium text-gray-800 text-sm">{estimate.product_name}</span>
              {estimate.brand && <span className="text-xs text-gray-400 ml-1">({estimate.brand})</span>}
            </div>
          </div>
          <p className="text-[11px] text-gray-400">{estimate.description}</p>
          {estimate.confidence !== 'high' && (
            <span className={`inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded-full ${
              estimate.confidence === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
            }`}>
              {estimate.confidence === 'medium' ? 'Mittlere Sicherheit' : 'Grobe Schätzung'}
            </span>
          )}
        </div>

        {/* Amount input */}
        <div>
          <WizardLabel>
            {estimate.unit === 'portion' ? 'Portionen' : `Menge (${estimate.unit})`}
          </WizardLabel>
          <div className="flex items-center gap-2 mt-1">
            <WizardInput
              value={amountG}
              onChange={setAmountG}
              type="number"
              step={estimate.unit === 'portion' ? '0.5' : '10'}
              min="1"
              placeholder={estimate.unit === 'portion' ? '1' : '100'}
            />
            {estimate.portion_size_g && estimate.unit !== 'portion' && (
              <button
                onClick={() => setAmountG(String(estimate.portion_size_g))}
                className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-200 transition-colors whitespace-nowrap"
              >
                {estimate.portion_label || `${estimate.portion_size_g}g`}
              </button>
            )}
          </div>
        </div>

        {/* Nutrition preview (computed) */}
        <div>
          <WizardLabel>Nährwerte</WizardLabel>
          <div className="grid grid-cols-4 gap-2 mt-1">
            <NutritionChip label="kcal" value={computedNutrition.kcal} color="emerald" />
            <NutritionChip label="Protein" value={`${computedNutrition.protein_g}g`} color="blue" />
            <NutritionChip label="Fett" value={`${computedNutrition.fat_g}g`} color="orange" />
            <NutritionChip label="Carbs" value={`${computedNutrition.carbs_g}g`} color="purple" />
          </div>
          <p className="text-[10px] text-gray-400 mt-1 text-center">
            Basis: {estimate.nutrition_per_100.kcal} kcal / 100{estimate.unit}
          </p>
        </div>

        {/* Save toggle */}
        {!estimate.product_id && !estimate.recipe_id && (
          <button onClick={() => setSaveAsProduct(!saveAsProduct)} className="w-full flex items-start gap-3 text-left">
            <div className={`mt-0.5 w-10 h-6 rounded-full transition-colors shrink-0 relative ${saveAsProduct ? 'bg-emerald-500' : 'bg-gray-300'}`}>
              <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${saveAsProduct ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-800">Lebensmittel merken</p>
              <p className="text-[11px] text-gray-400 leading-tight">Beim nächsten Mal sofort erkannt, ohne KI</p>
            </div>
          </button>
        )}
      </WizardModal>
    );
  }

  // ── Render: Input ──

  return (
    <WizardModal
      open={open}
      onClose={handleClose}
      title="Essen tracken"
      footer={
        <WizardButton onClick={handleEstimate} loading={loading} disabled={!description.trim() && !imageBase64}>
          {imageBase64 ? 'Bild analysieren' : 'Nährwerte schätzen'}
        </WizardButton>
      }
    >
      {error && <WizardError message={error} onRetry={() => setError(null)} />}

      <div>
        <WizardLabel>Was hast du gegessen?</WizardLabel>
        <textarea value={description} onChange={e => setDescription(e.target.value)}
          placeholder="z.B. TK-Pizza von Dr. Oetker, Pasta mit Pesto, Hummus-Wrap..."
          rows={2} autoFocus
          className="w-full mt-1 px-3 py-2 bg-gray-200 rounded-lg text-sm placeholder-gray-400 outline-none resize-none focus:ring-2 focus:ring-emerald-500"
        />
      </div>

      <div>
        <WizardLabel>Oder Foto hochladen</WizardLabel>
        {imagePreview ? (
          <div className="relative mt-1">
            <img src={imagePreview} alt="Preview" className="w-full h-32 object-cover rounded-lg" />
            <button onClick={removeImage}
              className="absolute top-1 right-1 w-6 h-6 bg-black/50 text-white rounded-full flex items-center justify-center text-xs hover:bg-black/70">
              x
            </button>
          </div>
        ) : (
          <button onClick={() => fileInputRef.current?.click()}
            className="w-full mt-1 py-4 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-400 hover:border-emerald-300 hover:text-emerald-500 transition-colors">
            Tippe hier um ein Foto auszuwählen
          </button>
        )}
        <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleImageSelect} className="hidden" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <WizardLabel>Mahlzeit</WizardLabel>
          <WizardSelect value={mealType} onChange={setMealType} options={MEAL_TYPES} />
        </div>
        <div>
          <WizardLabel>Datum</WizardLabel>
          <WizardInput type="date" value={date} onChange={setDate} />
        </div>
      </div>
    </WizardModal>
  );
}

function NutritionChip({ label, value, color }: { label: string; value: string | number; color: string }) {
  const colors: Record<string, string> = {
    emerald: 'bg-emerald-50 text-emerald-700',
    blue: 'bg-blue-50 text-blue-700',
    orange: 'bg-orange-50 text-orange-700',
    purple: 'bg-purple-50 text-purple-700',
  };
  return (
    <div className={`rounded-lg px-2 py-1.5 text-center ${colors[color] || 'bg-gray-50 text-gray-700'}`}>
      <p className="text-sm font-semibold">{value}</p>
      <p className="text-[10px] opacity-70">{label}</p>
    </div>
  );
}

function todayISO(): string {
  return new Date().toLocaleDateString('sv-SE');
}
